import { logger } from "../../../shared/utils/logger";
import { jwtDecode } from "jwt-decode";
import { apiFetch } from "../../../shared/utils/http";

import {
  getToken as getOidcToken,
  myGovLogout as oidcLogout,
  storeSessionOnServer,
} from "../../auth/AuthProvider";

import {
  getToken as getManualToken,
  manualLogout,
} from "../../auth/ManualAuthProvider";

import type { NavigateFunction } from "react-router-dom";

// -------------------------
// Shared Types
// -------------------------
export type AuthProvider = "MYGOV" | "EIDAS" | "MANUAL";
export type Provider = AuthProvider;
export type LoggedMethod = AuthProvider;

export type UserClaims = {
  sub?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  PublicServiceNumber?: string;
  BirthDate?: string;
};

export type AuthState = {
  isAuthenticated: boolean;
  provider: "MYGOV" | "EIDAS" | "MANUAL" | null;
  user: null | {
    email?: string;
    firstName?: string;
    lastName?: string;
    ppsn?: string;
    birthDate?: string;
  };
};

// -------------------------
// Storage Keys
// -------------------------
const JWT_TOKEN_KEY = "jwtToken";
const LOGIN_METHOD_KEY = "loggedMethod";

// -------------------------
// User Claims (server)
// -------------------------
export async function getUserClaimsFromServer(provider: "MYGOV" | "EIDAS" | "MANUAL" | null): Promise<UserClaims> {
  const res = await apiFetch("/session/token", { method: "GET", credentials: "include" });
  if (!res.ok) return {};
  const { token } = await res.json();
  if (!token) return {};

  try {
    return jwtDecode<UserClaims>(token);
  } catch {
    const s = await apiFetch("/session/status", { credentials: "include" }).then((r) => r.json());
    return s?.authenticated && s.user ? (s.user as UserClaims) : {};
  }
}

// -------------------------
// Login Method Flag
// -------------------------
export const storeLoginMethod = (method: LoggedMethod): void => {
  localStorage.setItem(LOGIN_METHOD_KEY, method);
  logger.debug(`Stored login method: ${method}`);
};

export const getLoggedMethod = (): LoggedMethod | null => {
  const method = localStorage.getItem(LOGIN_METHOD_KEY) as LoggedMethod | null;
  return method === "MYGOV" || method === "EIDAS" || method === "MANUAL" ? method : null;
};

export const clearLoginMethod = (): void => {
  localStorage.removeItem(LOGIN_METHOD_KEY);
  logger.debug("Cleared login method from localStorage");
};

// -------------------------
// Centralized Token Fetching
// -------------------------
export const getToken = async (): Promise<string | null> => {
  const method = getLoggedMethod();
  logger.debug("getToken() called. Detected login method:", method);

  if (method === "MYGOV" || method === "EIDAS") {
    logger.debug(`Using OIDC (${method}) authentication. Fetching token...`);
    const token = await getOidcToken();
    logger.debug("OIDC token fetched:", token ? token.substring(0, 20) + "..." : null);
    return token;
  }

  if (method === "MANUAL") {
    logger.debug("Using MANUAL authentication. Fetching token...");
    const token = await getManualToken();
    logger.debug("MANUAL token fetched:", token ? token.substring(0, 20) + "..." : null);
    return token;
  }

  logger.warn("Unknown login method. No token will be returned.");
  return null;
};

// -------------------------
// Logout
// -------------------------
export const logout = async (navigate: NavigateFunction): Promise<void> => {
  const method = getLoggedMethod();
  logger.debug("logout() called. Detected login method:", method);

  try {
    if (method === "MYGOV" || method === "EIDAS") {
      logger.debug(`Delegating logout to OIDC AuthProvider (${method})...`);
      await oidcLogout();
    } else if (method === "MANUAL") {
      logger.debug("Delegating logout to ManualAuthProvider (MANUAL)...");
      await manualLogout();
    } else {
      logger.warn("Unknown login method. No specific logout action taken.");
    }

    clearLoginData();
    logger.debug("Logout completed.");
  } catch (err) {
    logger.error("Error during logout:", err);
    navigate("/login");
  }
};

export const logoutSilent = async (): Promise<void> => {
  try {
    if ("sendBeacon" in navigator) {
      const blob = new Blob([], { type: "application/octet-stream" });
      navigator.sendBeacon("/session/logout", blob);
    } else {
      await fetch("/session/logout", {
        method: "POST",
        credentials: "include",
        keepalive: true,
      });
    }
  } catch {
    // ignore — we're leaving anyway
  } finally {
    try {
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("loggedMethod");
    } catch {}
  }
};

// -------------------------
// Clear Session/Data
// -------------------------
export const clearLoginData = (): void => {
  localStorage.removeItem(JWT_TOKEN_KEY);
  logger.debug("Cleared all user/session/procedure data from localStorage");
};

export const storeSessionByLoginMethod = async (): Promise<void> => {
  const method = getLoggedMethod();
  logger.debug("storeSessionByLoginMethod() — Detected login method:", method);

  if (method === "MYGOV") {
    const token = await getToken();
    if (token) {
      logger.debug("Storing MYGOV session with token...");
      await storeSessionOnServer(token);
    } else {
      logger.warn("No MYGOV token found.");
    }
    return;
  }

  if (method === "MANUAL") {
    const token = await getManualToken();
    if (token) {
      logger.debug("Storing MANUAL session with token...");
      await storeSessionOnServer(token);
    } else {
      logger.warn("No MANUAL token found.");
    }
    return;
  }

  logger.warn("Unknown or unset login method — skipping session storage.");
};

export async function getAuthStateFromSession(): Promise<AuthState> {
  try {
    const csrf = localStorage.getItem("csrfToken") ?? "";
    const r = await apiFetch("/session/status", {
      method: "GET",
      credentials: "include",
      headers: { "x-csrf-token": csrf },
    }, { skipLogoutOn401: true });

    if (!r.ok) {
      logger.debug("[session] /session/status not ok:", r.status);
      return { isAuthenticated: false, user: null, provider: null };
    }

    const data = await r.json();
    logger.debug("[session] /session/status payload:", data);

    if (!data?.authenticated) {
      logger.debug("[session] not authenticated");
      return { isAuthenticated: false, user: null, provider: null };
    }

    const u = data.user || {};
    return {
      isAuthenticated: true,
      provider: (u.method as any) ?? (localStorage.getItem("loggedMethod") as any) ?? null,
      user: {
        email: u.email || "",
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        ppsn: u.PublicServiceNumber || "",
        birthDate: u.BirthDate || "",
      },
    };
  } catch (e) {
    logger.debug("[session] /session/status error:", e);
    return { isAuthenticated: false, user: null, provider: null };
  }
}

export function computeAuthState(
    oidc?: { isAuthenticated?: boolean; user?: any | null },
    manual?: { isAuthenticated?: boolean; user?: any | null }
): AuthState {
  const oidcAuthed = !!oidc?.isAuthenticated && !!oidc?.user;
  const manualAuthed = !!manual?.isAuthenticated && !!manual?.user;

  logger.debug("[computeAuthState] in:", {
    oidc: { isAuthenticated: oidc?.isAuthenticated, hasUser: !!oidc?.user },
    manual: { isAuthenticated: manual?.isAuthenticated, hasUser: !!manual?.user },
  });

  if (oidcAuthed) {
    const out: AuthState = {
      isAuthenticated: true,
      user: oidc!.user ?? null,
      provider: (localStorage.getItem("loggedMethod") as any) ?? "MYGOV",
    };
    logger.debug("[computeAuthState] → OIDC", out);
    return out;
  }

  if (manualAuthed) {
    const out: AuthState = {
      isAuthenticated: true,
      user: manual!.user ?? null,
      provider: "MANUAL",
    };
    logger.debug("[computeAuthState] → MANUAL", out);
    return out;
  }

  const fallback: AuthState = { isAuthenticated: false, user: null, provider: (localStorage.getItem("loggedMethod") as any) ?? null };
  logger.debug("[computeAuthState] → unauthenticated", fallback);
  return fallback;
}
